import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAuthorizedTraders,
  fetchTraderDetail,
  updateTraderCommissions,
  fetchCryptoDeposits,
  fetchBalanceHistory,
  fetchPanelDepositStats,
  fetchPanelWithdrawalStats,
  updateTraderKassaPermission,
  fetchPendingTraderDeposits,
  approveTraderDeposit,
  rejectTraderDeposit,
  updateTraderTrcAddress,
  getTraderTrcAddress,
  fetchPendingTraderWithdrawals,
  approveTraderWithdrawal,
  rejectTraderWithdrawal,
  fetchAdminTraderWithdrawals,
} from "../thunks/TraderKassaManagement";

const initialState = {
  authorizedTraders: [],
  selectedTrader: null,
  traderDetail: null,
  cryptoDeposits: [],
  withdrawals: [],
  balanceHistory: [],
  panelDepositStats: null,
  panelWithdrawalStats: null,
  pendingDeposits: [],
  pendingWithdrawals: [],
  trcAddress: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
};

const traderKassaManagementSlice = createSlice({
  name: "traderKassaManagement",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedTrader: (state, action) => {
      state.selectedTrader = action.payload;
    },
    resetTraderDetail: (state) => {
      state.traderDetail = null;
      state.cryptoDeposits = [];
      state.withdrawals = [];
      state.balanceHistory = [];
      state.panelDepositStats = null;
      state.panelWithdrawalStats = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Authorized Traders
    builder.addCase(fetchAuthorizedTraders.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchAuthorizedTraders.fulfilled, (state, action) => {
      state.isLoading = false;
      state.authorizedTraders = action.payload.traders || [];
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(fetchAuthorizedTraders.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to fetch traders";
    });

    // Fetch Trader Detail
    builder.addCase(fetchTraderDetail.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTraderDetail.fulfilled, (state, action) => {
      state.isLoading = false;
      state.traderDetail = action.payload.trader;
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(fetchTraderDetail.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to fetch trader detail";
    });

    // Update Trader Commissions
    builder.addCase(updateTraderCommissions.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateTraderCommissions.fulfilled, (state, action) => {
      state.isLoading = false;
      if (state.traderDetail) {
        state.traderDetail.depositCommission =
          action.payload.trader.depositCommission;
        state.traderDetail.withdrawalCommission =
          action.payload.trader.withdrawalCommission;
      }
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(updateTraderCommissions.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to update commissions";
    });

    // Fetch Crypto Deposits
    builder.addCase(fetchCryptoDeposits.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchCryptoDeposits.fulfilled, (state, action) => {
      state.isLoading = false;
      state.cryptoDeposits = action.payload.deposits || [];
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(fetchCryptoDeposits.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to fetch crypto deposits";
    });

    // Fetch Balance History
    builder.addCase(fetchBalanceHistory.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchBalanceHistory.fulfilled, (state, action) => {
      state.isLoading = false;
      state.balanceHistory = action.payload.history || [];
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(fetchBalanceHistory.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to fetch balance history";
    });

    // Fetch Panel Deposit Stats
    builder.addCase(fetchPanelDepositStats.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchPanelDepositStats.fulfilled, (state, action) => {
      state.isLoading = false;
      state.panelDepositStats = action.payload;
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(fetchPanelDepositStats.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to fetch deposit stats";
    });

    // Fetch Panel Withdrawal Stats
    builder.addCase(fetchPanelWithdrawalStats.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchPanelWithdrawalStats.fulfilled, (state, action) => {
      state.isLoading = false;
      state.panelWithdrawalStats = action.payload;
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(fetchPanelWithdrawalStats.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to fetch withdrawal stats";
    });

    // Update Trader Kassa Permission
    builder.addCase(updateTraderKassaPermission.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateTraderKassaPermission.fulfilled, (state) => {
      state.isLoading = false;
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(updateTraderKassaPermission.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to update permission";
    });

    // Fetch Pending Trader Deposits
    builder.addCase(fetchPendingTraderDeposits.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchPendingTraderDeposits.fulfilled, (state, action) => {
      state.isLoading = false;
      state.pendingDeposits = action.payload.deposits || [];
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(fetchPendingTraderDeposits.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to fetch pending deposits";
    });

    // Approve Trader Deposit
    builder.addCase(approveTraderDeposit.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(approveTraderDeposit.fulfilled, (state, action) => {
      state.isLoading = false;
      // Remove approved deposit from pending list
      state.pendingDeposits = state.pendingDeposits.filter(
        (deposit) => deposit._id !== action.payload.deposit._id
      );
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(approveTraderDeposit.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to approve deposit";
    });

    // Reject Trader Deposit
    builder.addCase(rejectTraderDeposit.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(rejectTraderDeposit.fulfilled, (state, action) => {
      state.isLoading = false;
      // Remove rejected deposit from pending list
      state.pendingDeposits = state.pendingDeposits.filter(
        (deposit) => deposit._id !== action.payload.deposit._id
      );
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(rejectTraderDeposit.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to reject deposit";
    });

    // Update Trader TRC Address
    builder.addCase(updateTraderTrcAddress.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateTraderTrcAddress.fulfilled, (state, action) => {
      state.isLoading = false;
      if (state.traderDetail) {
        // Update cryptoWallet object as it comes from backend
        if (!state.traderDetail.cryptoWallet) {
          state.traderDetail.cryptoWallet = {};
        }
        state.traderDetail.cryptoWallet.address =
          action.payload.trader.cryptoWallet?.address;
        state.traderDetail.cryptoWallet.network =
          action.payload.trader.cryptoWallet?.network;
      }
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(updateTraderTrcAddress.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to update TRC address";
    });

    // Get Trader TRC Address
    builder.addCase(getTraderTrcAddress.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getTraderTrcAddress.fulfilled, (state, action) => {
      state.isLoading = false;
      state.trcAddress = action.payload.trader;
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(getTraderTrcAddress.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to fetch TRC address";
    });

    // Fetch Pending Trader Withdrawals
    builder.addCase(fetchPendingTraderWithdrawals.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      fetchPendingTraderWithdrawals.fulfilled,
      (state, action) => {
        state.isLoading = false;
        state.pendingWithdrawals = action.payload.withdrawals || [];
        state.lastUpdated = new Date().toISOString();
      }
    );
    builder.addCase(fetchPendingTraderWithdrawals.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to fetch pending withdrawals";
    });

    // Approve Trader Withdrawal
    builder.addCase(approveTraderWithdrawal.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(approveTraderWithdrawal.fulfilled, (state, action) => {
      state.isLoading = false;
      // Remove approved withdrawal from pending list
      state.pendingWithdrawals = state.pendingWithdrawals.filter(
        (withdrawal) => withdrawal._id !== action.payload.withdrawal._id
      );
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(approveTraderWithdrawal.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to approve withdrawal";
    });

    // Reject Trader Withdrawal
    builder.addCase(rejectTraderWithdrawal.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(rejectTraderWithdrawal.fulfilled, (state, action) => {
      state.isLoading = false;
      // Remove rejected withdrawal from pending list
      state.pendingWithdrawals = state.pendingWithdrawals.filter(
        (withdrawal) => withdrawal._id !== action.payload.withdrawal._id
      );
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(rejectTraderWithdrawal.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to reject withdrawal";
    });

    // Fetch Trader Withdrawals (Admin)
    builder.addCase(fetchAdminTraderWithdrawals.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchAdminTraderWithdrawals.fulfilled, (state, action) => {
      state.isLoading = false;
      state.withdrawals = action.payload.withdrawals || [];
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(fetchAdminTraderWithdrawals.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to fetch withdrawals";
    });
  },
});

export const { clearError, setSelectedTrader, resetTraderDetail } =
  traderKassaManagementSlice.actions;

export const traderKassaManagementReducer = traderKassaManagementSlice.reducer;
